// AI Transaction Parser for categorizing voice input
const INCOME_CATEGORY_KEYS = [
  'nailServices',
  'hairServices',
  'skinServices',
  'injectables',
  'bodyTreatments',
  'makeupServices',
  'massageServices',
  'otherServices',
  'productSales',
  'govFunding',
  'loanIncome',
  'directorLoanIncome'
];

class TransactionParser {
  constructor() {
    // Keywords for different categories
    this.categoryKeywords = {
      // Income categories
      nailServices: [
        'nails', 'nail', 'manicure', 'pedicure', 'manucure', 'manucures', 'gel nails', 'acrylic nails',
        'nail art', 'nail polish', 'nail extension', 'shellac', 'sns', 'dip powder'
      ],
      hairServices: [
        'hair', 'haircut', 'cut', 'color', 'colour', 'highlights', 'lowlights',
        'perm', 'straightening', 'blowdry', 'blow dry', 'styling', 'updo',
        'hair treatment', 'keratin', 'balayage', 'ombre', 'barber', 'barbers',
        'barbering', 'beard trim', 'mens cut', "men's cut"
      ],
      skinServices: [
        'facial', 'face', 'skin', 'cleansing', 'exfoliation', 'mask',
        'anti-aging', 'acne treatment', 'microdermabrasion', 'chemical peel'
      ],
      injectables: [
        'injectable', 'injectables', 'botox', 'anti wrinkle', 'anti-wrinkle',
        'dermal filler', 'filler', 'fillers', 'skin booster', 'profhilo'
      ],
      bodyTreatments: [
        'body treatment', 'body treatments', 'body contouring', 'body sculpting',
        'body wrap', 'body scrub', 'lymphatic drainage', 'cellulite treatment'
      ],
      makeupServices: [
        'makeup', 'make up', 'foundation', 'eyeshadow', 'lipstick',
        'bridal makeup', 'evening makeup', 'special occasion'
      ],
      massageServices: [
        'massage', 'relaxing massage', 'deep tissue', 'hot stone',
        'aromatherapy', 'swedish massage', 'sports massage'
      ],
      otherServices: [
        'eyebrow', 'brow', 'eyelash', 'lash', 'waxing', 'threading',
        'tinting', 'lash lift', 'brow lamination'
      ],
      productSales: [
        'product', 'products', 'merchandise', 'retail', 'shampoo', 'conditioner',
        'hair product', 'nail polish', 'cosmetics', 'beauty product', 'cream',
        'lotion', 'serum', 'oil', 'treatment', 'mask', 'scrub', 'cleanser',
        'moisturizer', 'moisturiser', 'toner', 'perfume', 'fragrance', 'gift card', 'voucher'
      ],
      govFunding: [
        'gov funding', 'government funding', 'government grant', 'government grants',
        'grant funding', 'grant', 'grants'
      ],
      loanIncome: [
        'loan', 'business loan', 'loan income', 'loan received', 'loan drawdown',
        'loan advance', 'loan proceeds', 'loan from'
      ],
      directorLoanIncome: [
        'director loan received', 'director loan income', 'director loan introduced',
        'director loan advance', 'director loan from director', "director's loan received",
        "director's loan introduced"
      ],

      // Expense categories - Supplies & Materials
      supplies: [
        'supplies', 'materials', 'products', 'shampoo', 'conditioner',
        'hair products', 'nail polish', 'gel', 'clips', 'foils',
        'cotton', 'towels', 'gloves', 'masks', 'creams', 'lotions',
        'mask', 'face mask', 'hair mask', 'brow tint', 'eyebrow tint',
        'lash tint', 'tint', 'tinting product', 'brow dye', 'eyebrow dye',
        'wax', 'hot wax', 'strip wax', 'soft wax', 'hard wax',
        'hair spray', 'hairspray', 'nail varnish', 'nail file', 'nail files',
        'nail tips', 'nail tip', 'gel tips', 'acrylic tips'
      ],
      
      // Equipment & Tools (Capital expenses)
      equipment: [
        'equipment', 'machine', 'laser', 'steamer', 'chair', 'bed',
        'trolley', 'cabinet', 'mirror', 'lighting', 'hair dryer', 'dryer',
        'straightener', 'curling iron', 'scissors', 'clippers'
      ],
      salon_tools: [
        'salon tools', 'salon tool', 'brush', 'comb', 'drill',
        'lamp', 'uv lamp', 'led lamp', 'tweezers', 'spatula'
      ],
      electronics: [
        'computer', 'laptop', 'macbook', 'tablet', 'ipad', 'phone',
        'mobile phone', 'smartphone', 'iphone', 'samsung', 'galaxy',
        'google pixel', 'pixel', 'printer', 'camera', 'airpods',
        'apple watch', 'sound system', 'tv', 'monitor'
      ],
      furniture: [
        'chair', 'table', 'desk', 'shelf', 'storage', 'sofa',
        'reception desk', 'waiting chair'
      ],

      // Operating expenses
      rent: ['rent', 'lease', 'rental'],
      utilities: ['electricity', 'gas', 'water', 'utility bill'],
      internet_telephone: [
        'internet', 'internet bill', 'broadband', 'broadband bill', 'wifi', 'wi-fi',
        'telephone', 'telephone bill', 'phone', 'phone bill', 'mobile bill',
        'telecom', 'telecommunications', 'eir', 'vodafone', 'three', 'virgin media'
      ],
      insurance: ['insurance', 'liability', 'coverage'],
      marketing: ['advertising', 'marketing', 'social media', 'website', 'flyers'],
      employer_prsi: ['prsi', 'employer prsi', "employer's prsi", 'employer paye prsi'],
      employee_holiday_pay: [
        'holiday pay', 'employee holiday pay', 'staff holiday pay', 'holiday pay reserve', 'holiday reserve'
      ],
      owner_pension: ['owner pension', 'owners pension', "owner's pension"],
      naersa_employer_contributions: [
        'naersa employer contributions', 'naersa employer contribution', 'naersa employer'
      ],
      naersa_employee_contributions: [
        'naersa employee contributions', 'naersa employee contribution', 'naersa employee'
      ],
      staffCosts: [
        'salary', 'salaries', 'wage', 'wages', 'staff', 'staff costs',
        'employee', 'contractor', 'payroll', 'labour', 'labour costs',
        'labor', 'labor costs'
      ],
      general_expenses: ['general expense', 'general expenses', 'miscellaneous expense', 'sundry'],
      interest: [
        'interest', 'interest paid', 'bank interest', 'loan interest',
        'finance interest', 'interest expense'
      ],
      bank_charges: [
        'bank charge', 'bank charges', 'bank fee', 'bank fees',
        'account fee', 'account fees', 'transaction fee', 'transaction fees',
        'merchant fee', 'merchant fees'
      ],
      tips: ['tip', 'tips', 'gratuity', 'gratuities', 'staff tip', 'staff tips'],
      director_loan: [
        'director loan repayment', 'director loan payment', 'director loan expense',
        'director loan paid', 'paid director', 'paid back director',
        "director's loan repayment", "director's loan payment"
      ],
      loan_repayment: [
        'loan repayment', 'loan repayments', 'loan payment', 'loan payments',
        'repayment', 'repayments', 'finance repayment'
      ],
      
      // Professional services
      accounting: ['accountant', 'bookkeeping', 'tax preparation', 'tax return', 'accounting'],
      legal: ['lawyer', 'legal', 'solicitor', 'contract'],
      
      // Travel & Transportation
      vehicle_fuel: [
        'vehicle fuel', 'car fuel', 'diesel', 'petrol', 'fuel', 'gasoline',
        'unleaded', 'fuel station', 'pump', 'circle k', 'applegreen',
        'topaz', 'maxol'
      ],
      vehicle_repairs_licensing: [
        'garage', 'mechanic', 'tyre', 'tire', 'oil change', 'brake',
        'windscreen', 'wheel alignment', 'car service', 'vehicle service',
        'motor tax', 'road tax', 'vehicle licensing', 'vehicle licence',
        'vehicle license', 'nct', 'mot'
      ],
      travel_expenses: [
        'train', 'bus', 'taxi', 'uber', 'lyft',
        'transport', 'transportation', 'travel', 'journey',
        'parking', 'toll', 'mileage', 'car rental'
      ],
      meals: [
        'meal', 'meals', 'food', 'drink', 'drinks', 'coffee', 'tea',
        'cafe', 'restaurant', 'lunch', 'dinner', 'breakfast', 'sandwich',
        'snack', 'takeaway', 'water', 'juice'
      ],
      
      // Other
      office: ['paper', 'pens', 'stationery', 'office supplies'],
      software: ['software', 'app', 'subscription', 'license'],
      training: ['course', 'training', 'education', 'workshop', 'seminar']
    };

    // Payment method keywords
    this.paymentKeywords = {
      cash: ['cash', 'money'],
      card: ['card', 'credit card', 'debit card', 'visa', 'mastercard'],
      revolut: ['revolut'],
      transfer: ['transfer', 'bank transfer', 'wire'],
      voucher: ['voucher', 'gift card', 'gift voucher']
    };

    // Transaction type keywords
    this.transactionTypeKeywords = {
      income: [
        'sale', 'sold', 'service', 'treatment', 'client', 'customer', 'injectable',
        'botox', 'filler', 'gov funding', 'government funding', 'grant', 'funding',
        'funding from', 'loan', 'loan received', 'business loan', 'loan from',
        'director loan received', 'director loan introduced', "director's loan received"
      ],
      expense: [
        'purchase', 'bought', 'buy', 'expense', 'cost', 'paid for', 'from',
        'tip', 'tips', 'gratuity', 'loan repayment', 'loan payment', 'repayment',
        'bank charge', 'bank charges', 'bank fee', 'bank fees', 'interest paid',
        'director loan repayment', 'director loan payment', 'paid back director'
      ]
    };

    // Currency symbols and patterns
    this.currencyPatterns = [
      /€\s*(\d+(?:\.\d{2})?)/g,  // Euro
      /\$(\d+(?:\.\d{2})?)/g,  // Dollar
      /£\s*(\d+(?:\.\d{2})?)/g,   // Pound
      /(\d+(?:\.\d{2})?) ?euros?/gi,
      /(\d+(?:\.\d{2})?) ?dollars?/gi,
      /(\d+(?:\.\d{2})?) ?pounds?/gi
    ];

    // Month detection for date parsing / exclusion
    this.monthPattern = /(january|february|march|april|may|june|july|august|september|sept|october|november|december|jan|feb|mar|apr|jun|jul|aug|sep|oct|nov|dec)/i;

    this.modelProductKeywords = [
      'iphone', 'ipad', 'macbook', 'apple watch', 'airpods',
      'samsung galaxy', 'samsung', 'galaxy', 'google pixel', 'pixel',
      'phone', 'mobile phone', 'smartphone', 'tablet', 'laptop',
      'computer', 'printer', 'camera', 'monitor', 'tv'
    ];

    this.modelContinuationWords = [
      'pro', 'max', 'mini', 'plus', 'ultra', 'se', 'air', 'series',
      'galaxy', 'note', 'edge', 'fold', 'flip'
    ];

    this.modelAccessoryWords = [
      'case', 'cover', 'charger', 'screen protector', 'protector'
    ];
  }

  escapeRegExp(value) {
    return String(value || '').replace(/[.*+?^${}()|[\]\\]/g, '\\$&');
  }

  getModelProductPattern() {
    return this.modelProductKeywords
      .map((keyword) => this.escapeRegExp(keyword).replace(/\s+/g, '\\s+'))
      .join('|');
  }

  getModelTokenPattern() {
    const continuationPattern = this.modelContinuationWords
      .map((word) => this.escapeRegExp(word))
      .join('|');
    return `(?:\\d+[a-z]*|${continuationPattern})`;
  }

  isKnownModelProductName(value) {
    if (!value) return false;
    return new RegExp(`\\b(?:${this.getModelProductPattern()})\\b`, 'i').test(value);
  }

  isStrongModelProductName(value) {
    if (!value) return false;
    return /\b(iphone|ipad|macbook|apple\s+watch|airpods|samsung(?:\s+galaxy)?|galaxy|google\s+pixel|pixel)\b/i.test(value);
  }

  isKnownModelPhrase(value) {
    if (!value) return false;
    const modelPattern = new RegExp(
      `\\b(?:${this.getModelProductPattern()})(?:\\s+${this.getModelTokenPattern()}){1,6}\\b`,
      'i'
    );
    return modelPattern.test(value);
  }

  isKnownModelItemName(value) {
    if (!value) return false;
    if (this.isKnownModelPhrase(value)) return true;
    const accessoryPattern = this.modelAccessoryWords
      .map((word) => this.escapeRegExp(word).replace(/\s+/g, '\\s+'))
      .join('|');
    const modelAccessoryPattern = new RegExp(
      `\\b(?:${this.getModelProductPattern()})(?:\\s+${this.getModelTokenPattern()}){1,5}\\s+(?:${accessoryPattern})\\b`,
      'i'
    );
    return modelAccessoryPattern.test(value);
  }

  isModelContinuationText(value) {
    if (!value) return false;
    const tokens = value.toLowerCase().trim().split(/\s+/).filter(Boolean);
    if (!tokens.length || tokens.length > 4) return false;
    const allowed = new Set([...this.modelContinuationWords, ...this.modelAccessoryWords, 'for', 'at']);
    return tokens.every((token) => allowed.has(token));
  }

  getModelPrefixBefore(text, index) {
    if (!text || typeof index !== 'number' || index <= 0) return '';
    const before = text.slice(0, index).replace(/\s+/g, ' ').trim();
    if (!before) return '';
    const prefixPattern = new RegExp(
      `\\b(?:${this.getModelProductPattern()})(?:\\s+${this.getModelTokenPattern()}){1,5}\\s*$`,
      'i'
    );
    const match = before.match(prefixPattern);
    return match ? match[0].trim() : '';
  }

  getLaterAmountPattern() {
    return /(?:[\u20ac\u00a3$]\s*\d+(?:\.\d{1,2})?|\d+(?:\.\d{1,2})?\s*(?:euros?|dollars?|pounds?)|\b\d+(?:\.\d{1,2})?\s+(?:paid|cash|card|revolut|paypal|bank)\b)/i;
  }

  isLikelyModelNumberAmount(itemName, amount, fullText, index) {
    if (/\d/.test(itemName || '') && this.isKnownModelItemName(itemName)) {
      return false;
    }
    const numericAmount = Number(amount);
    if (!Number.isInteger(numericAmount) || numericAmount < 2 || numericAmount > 100) {
      return false;
    }
    if (!this.isKnownModelProductName(itemName)) {
      return false;
    }

    const escapedItem = this.escapeRegExp(itemName).replace(/\s+/g, '\\s+');
    const phrasePattern = new RegExp(`\\b${escapedItem}\\s+${numericAmount}\\b`, 'i');
    const textFromMatch = typeof index === 'number' ? fullText.slice(index) : fullText;
    if (!phrasePattern.test(textFromMatch)) {
      return false;
    }

    const phraseMatch = textFromMatch.match(phrasePattern);
    const afterPhrase = phraseMatch
      ? textFromMatch.slice(phraseMatch.index + phraseMatch[0].length)
      : '';
    return this.isStrongModelProductName(itemName) || this.getLaterAmountPattern().test(afterPhrase);
  }

  isLikelyModelQuantityResult(itemName, quantity, rawText) {
    const numericQuantity = Number(quantity);
    if (!Number.isInteger(numericQuantity) || numericQuantity < 2 || numericQuantity > 100) {
      return false;
    }
    if (!this.isKnownModelProductName(itemName)) {
      return false;
    }
    const escapedItem = this.escapeRegExp(itemName).replace(/\s+/g, '\\s+');
    return new RegExp(`\\b${escapedItem}\\s+${numericQuantity}\\b`, 'i').test(rawText || '');
  }

  isNumberPartOfKnownModel(text, index, numberText) {
    const numericAmount = Number(numberText);
    if (!Number.isInteger(numericAmount) || numericAmount < 2 || numericAmount > 100) {
      return false;
    }

    const before = text.slice(Math.max(0, index - 80), index).replace(/\s+/g, ' ').trim();
    if (!before) return false;
    const candidate = `${before} ${numberText}`;
    const anchoredModelPattern = new RegExp(
      `\\b(?:${this.getModelProductPattern()})(?:\\s+${this.getModelTokenPattern()}){1,6}\\s*$`,
      'i'
    );
    return anchoredModelPattern.test(candidate);
  }

  resolveModelItemName(itemName, fullText, index) {
    const cleanItem = String(itemName || '').trim();
    if (!cleanItem) return cleanItem;

    const modelPrefix = this.getModelPrefixBefore(fullText, index);
    if (!modelPrefix) return cleanItem;

    const lowerItem = cleanItem.toLowerCase();
    if (/^(for|at)$/.test(lowerItem)) {
      return modelPrefix;
    }
    if (this.isModelContinuationText(lowerItem)) {
      return `${modelPrefix} ${cleanItem}`.replace(/\s+/g, ' ').trim();
    }
    return cleanItem;
  }

  isFoodOrDrinkCategory(category) {
    return ['meals', 'food', 'food_and_drink'].includes(String(category || '').toLowerCase());
  }

  applyNonReclaimableFoodVat(result) {
    if (!result || !this.isFoodOrDrinkCategory(result.category)) {
      return result;
    }

    const grossAmount = Number(result.amount) || 0;
    result.amount = grossAmount;
    result.netAmount = grossAmount;
    result.vatAmount = 0;
    result.vatRate = 0;
    result.vatComponents = [];

    if (Array.isArray(result.items)) {
      result.items = result.items.map((item) => {
        if (!item || typeof item !== 'object') return item;
        return {
          ...item,
          category: item.category || result.category,
          vatAmount: 0,
          vatRate: 0,
          vatReclaimable: false
        };
      });
    }

    return result;
  }

  // Remove date-like phrases so day numbers aren't treated as money
  removeDatePhrases(text) {
    let cleaned = text;
    // 15th november / 15 november
    cleaned = cleaned.replace(/\b(\d{1,2})(st|nd|rd|th)?\s+(january|february|march|april|may|june|july|august|september|sept|october|november|december|jan|feb|mar|apr|jun|jul|aug|sep|oct|nov|dec)\b/gi, ' ');
    // november 15th / nov 15
    cleaned = cleaned.replace(/\b(january|february|march|april|may|june|july|august|september|sept|october|november|december|jan|feb|mar|apr|jun|jul|aug|sep|oct|nov|dec)\s+\d{1,2}(st|nd|rd|th)?\b/gi, ' ');
    // Standalone ordinals like "20th" (often used when month already implied)
    cleaned = cleaned.replace(/\b\d{1,2}(st|nd|rd|th)\b/gi, ' ');
    return cleaned;
  }

  // Handle quantity x unit price phrases (e.g. "5 bottles ... 30 euro each")
  extractQuantityPriceTotals(text) {
    const wordToNumber = (word) => {
      if (!word) return NaN;
      const map = {
        one: 1, two: 2, three: 3, four: 4, five: 5, six: 6, seven: 7, eight: 8, nine: 9, ten: 10,
        eleven: 11, twelve: 12, thirteen: 13, fourteen: 14, fifteen: 15, sixteen: 16,
        seventeen: 17, eighteen: 18, nineteen: 19, twenty: 20, thirty: 30, forty: 40, fifty: 50
      };
      return map[word.toLowerCase()] ?? NaN;
    };

    const parseNumeric = (value) => {
      if (!value) return NaN;
      const normalized = value.toString().trim().toLowerCase();

      const directWord = wordToNumber(normalized);
      if (!isNaN(directWord)) return directWord;

      const wordParts = normalized
        .replace(/[^a-z\s-]/g, ' ')
        .trim()
        .split(/[\s-]+/)
        .filter(Boolean);

      if (wordParts.length === 2) {
        const tens = wordToNumber(wordParts[0]);
        const ones = wordToNumber(wordParts[1]);
        if (!isNaN(tens) && !isNaN(ones) && tens >= 20 && ones < 10) {
          return tens + ones;
        }
      }

      const cleaned = normalized.replace(/[^\d.]/g, '');
      if (!cleaned) return NaN;
      return parseFloat(cleaned);
    };
    const textWithoutDates = this.removeDatePhrases(text);

    const qty = '(\\d+|one|two|three|four|five|six|seven|eight|nine|ten|eleven|twelve|thirteen|fourteen|fifteen|sixteen|seventeen|eighteen|nineteen|twenty|thirty|forty|fifty)';
    const numberWord = '(?:\\d+(?:\\.\\d{1,2})?|one|two|three|four|five|six|seven|eight|nine|ten|eleven|twelve|thirteen|fourteen|fifteen|sixteen|seventeen|eighteen|nineteen|twenty|thirty|forty|fifty)';
    const price = `(?:\\u20ac|\\u00a3|\\$)?(${numberWord})`;
    const items = '(?:units?|items?|products?|services?|moisturisers?|moisturizers?|shampoos?|conditioners?|lotions?|creams?|haircuts?|haircut|cuts?|sessions?|clients?|massages?|manicures?|manucures?|pedicures?|nail files?|hairbrushes?|brushes?|brush|files?|file|sprays?|spray)';
    const containers = '(?:bottles?|bottle|packs?|pack|pieces?|piece|pcs|units?|unit|items?|item|products?|product|services?|service|clients?|client|sessions?|session)';
    const packContainers = '(?:box|boxes|pack|packs|bag|bags|bottle|bottles|set|sets|bundle|bundles|case|cases|roll|rolls|piece|pieces|pcs|unit|units|item|items)';
    const packContainerWords = [
      'box', 'boxes', 'pack', 'packs', 'bag', 'bags', 'bottle', 'bottles', 'set', 'sets',
      'bundle', 'bundles', 'case', 'cases', 'roll', 'rolls', 'piece', 'pieces', 'pcs', 'unit',
      'units', 'item', 'items'
    ];
    const ofItem = '([a-zA-Z\\s]+?)';
    const itemBeforeQty = '([a-zA-Z\\s]+?)';
    const quantityBoundary = '(?:\\d+|one|two|three|four|five|six|seven|eight|nine|ten|eleven|twelve|thirteen|fourteen|fifteen|sixteen|seventeen|eighteen|nineteen|twenty|thirty|forty|fifty)';
    const normalizedText = textWithoutDates.replace(
      new RegExp(`\\b(each|per)\\b\\s+(?=${quantityBoundary}\\b)`, 'gi'),
      '$1, '
    );

    const quantityPatterns = [
      { regex: new RegExp(`${qty}\\s+(${packContainers})\\s+${ofItem}\\b[^0-9a-z]*?(?:at|for)?\\s*${price}\\s*(?:euro|euros?|dollars?|pounds?)?\\s*(?:each|per)\\b`, 'gi'), itemGroup: 3, quantityGroup: 1, priceGroup: 4, priceType: 'unit', packGroup: 2 },
      { regex: new RegExp(`${qty}\\s+(${packContainers})\\s+${ofItem}\\b[^0-9a-z]*?(?:total|in\\s+total|for\\s+a\\s+total\\s+of)?\\s*${price}\\s*(?:euro|euros?|dollars?|pounds?)?`, 'gi'), itemGroup: 3, quantityGroup: 1, priceGroup: 4, priceType: 'total', packGroup: 2 },
      { regex: new RegExp(`${itemBeforeQty}\\s+(${packContainers})\\s+${qty}\\b[^0-9a-z]*?(?:at|for)?\\s*${price}\\s*(?:euro|euros?|dollars?|pounds?)?\\s*(?:each|per)?`, 'gi'), itemGroup: 1, quantityGroup: 3, priceGroup: 4, priceType: 'unit', packGroup: 2 },
      { regex: new RegExp(`${itemBeforeQty}\\s+(${packContainers})\\s+${qty}\\b[^0-9a-z]*?(?:total|in\\s+total|for\\s+a\\s+total\\s+of)\\s+${price}\\s*(?:euro|euros?|dollars?|pounds?)?`, 'gi'), itemGroup: 1, quantityGroup: 3, priceGroup: 4, priceType: 'total', packGroup: 2 },
      { regex: new RegExp(`${itemBeforeQty}\\s+${qty}\\s+(${packContainers})\\b[^0-9a-z]*?(?:at|for)?\\s*${price}\\s*(?:euro|euros?|dollars?|pounds?)?\\s*(?:each|per)?`, 'gi'), itemGroup: 1, quantityGroup: 2, priceGroup: 4, priceType: 'unit', packGroup: 3 },
      { regex: new RegExp(`${itemBeforeQty}\\s+${qty}\\s+(${packContainers})\\b[^0-9a-z]*?(?:total|in\\s+total|for\\s+a\\s+total\\s+of)\\s+${price}\\s*(?:euro|euros?|dollars?|pounds?)?`, 'gi'), itemGroup: 1, quantityGroup: 2, priceGroup: 4, priceType: 'total', packGroup: 3 },
      { regex: new RegExp(`${qty}\\s+(${items})\\b[^0-9a-z]*?${price}\\s*(?:euro|euros?|dollars?|pounds?)?\\s*(?:each|per)?`, 'gi'), itemGroup: 2, quantityGroup: 1, priceGroup: 3, priceType: 'unit' },
      { regex: new RegExp(`${qty}\\s+(${items})\\b[^0-9a-z]*?(?:at|for)\\s+${price}[a-zA-Z\\.]*\\s*(?:euro|euros?|dollars?|pounds?)?\\s*(?:each)?`, 'gi'), itemGroup: 2, quantityGroup: 1, priceGroup: 3, priceType: 'unit' },
      { regex: new RegExp(`${qty}\\s+(${containers})\\s+of\\s+${ofItem}\\b[^0-9a-z]*?(?:at|for)?\\s*${price}\\s*(?:euro|euros?|dollars?|pounds?)?\\s*(?:each|per)?`, 'gi'), itemGroup: 3, quantityGroup: 1, priceGroup: 4, priceType: 'unit', packGroup: 2 },
      { regex: new RegExp(`${qty}\\s+(${containers})\\s+of\\s+${ofItem}\\b[^0-9a-z]*?(?:total|in\\s+total|for\\s+a\\s+total\\s+of)\\s+${price}\\s*(?:euro|euros?|dollars?|pounds?)?`, 'gi'), itemGroup: 3, quantityGroup: 1, priceGroup: 4, priceType: 'total', packGroup: 2 },
      { regex: new RegExp(`${qty}\\s+${ofItem}\\b[^0-9a-z]*?(?:total|in\\s+total|for\\s+a\\s+total\\s+of)\\s+${price}\\s*(?:euro|euros?|dollars?|pounds?)?`, 'gi'), itemGroup: 2, quantityGroup: 1, priceGroup: 3, priceType: 'total' },
      { regex: new RegExp(`${qty}\\s*[xX]\\s*${price}\\s*(?:euro|euros?|dollars?|pounds?)?\\b`, 'gi'), itemGroup: null, quantityGroup: 1, priceGroup: 2, priceType: 'unit' }
    ];

    const results = [];
    const seenRaw = new Set();
    const seenValues = new Set();
    const stripPackSuffix = (value, packTypeValue) => {
      if (!value) return '';
      const lowered = value.toLowerCase().trim();
      if (!packTypeValue) return value;
      const pack = packTypeValue.toLowerCase().trim();
      if (!pack) return value;
      const regex = new RegExp(`\\s+${pack}\\s*$`, 'i');
      const cleaned = value.replace(regex, '').trim();
      return cleaned || value;
    };

    const isContainerOnly = (value) => {
      if (!value) return false;
      const lowered = value.toLowerCase().trim();
      return packContainerWords.includes(lowered);
    };

    const addResult = (quantity, unitPrice, item, index, rawText, totalOverride, packType) => {
      let cleanItem = (item || '').replace(/\s{2,}/g, ' ').trim();
      if (!cleanItem) return;
      cleanItem = stripPackSuffix(cleanItem, packType);
      cleanItem = cleanItem.replace(/^of\s+/i, '');
      if (this.isLikelyModelQuantityResult(cleanItem, quantity, rawText)) return;
      const normalizedItem = cleanItem.toLowerCase();
      if (isContainerOnly(normalizedItem)) return;
      const valueKey = `${quantity}|${unitPrice}|${normalizedItem}`;
      if (seenValues.has(valueKey)) return;
      const total = typeof totalOverride === 'number'
        ? parseFloat(totalOverride.toFixed(2))
        : parseFloat((quantity * unitPrice).toFixed(2));
      const rawKey = `${index}:${rawText}`;
      if (seenRaw.has(rawKey)) return;
      results.push({
        quantity,
        unitPrice,
        total,
        item: cleanItem,
        unitsPerPack: packType ? quantity : null,
        packType: packType || '',
        index,
        source: rawText
      });
      seenRaw.add(rawKey);
      seenValues.add(valueKey);
    };

    for (const { regex, itemGroup, quantityGroup, priceGroup, priceType, packGroup } of quantityPatterns) {
      let match;
      while ((match = regex.exec(normalizedText)) !== null) {
        const quantityRaw = match[quantityGroup || 1];
        const matchedItem = itemGroup ? match[itemGroup] || '' : '';
        const unitPriceRaw = match[priceGroup];
        const rawText = match[0];
        const packTypeRaw = packGroup ? match[packGroup] : '';
        const packType = packTypeRaw ? packTypeRaw.toLowerCase() : '';

        if (priceType === 'unit' && /\b(total|in\s+total|for\s+a\s+total\s+of)\b/i.test(rawText)) {
          continue;
        }
        if (priceType === 'total' && /\b(each|per)\b/i.test(rawText)) {
          continue;
        }
        if (priceType === 'total') {
          const trailingText = normalizedText.slice(regex.lastIndex, regex.lastIndex + 12);
          if (/\b(each|per)\b/i.test(trailingText)) {
            continue;
          }
        }

        const quantity = isNaN(parseFloat(quantityRaw)) ? wordToNumber(quantityRaw) : parseFloat(quantityRaw);
        const priceValue = parseNumeric(unitPriceRaw);
        if (!isNaN(quantity) && !isNaN(priceValue) && quantity > 0 && priceValue > 0) {
          const unitPrice = priceType === 'total' ? priceValue / quantity : priceValue;
          const total = priceType === 'total' ? priceValue : null;
          addResult(quantity, unitPrice, matchedItem.trim(), match.index, rawText, total, packType);
        }
      }
    }

    const segments = normalizedText.split(/\s+and\s+|,\s*/);
    let searchStart = 0;
    const segmentPattern = new RegExp(`^\\s*${qty}\\s+(.+?)\\s+${price}\\s*(?:euro|euros?|dollars?|pounds?)?\\s*(?:each|per)?\\s*$`, 'i');

    for (const segment of segments) {
      const trimmed = segment.trim();
      if (!trimmed) continue;

      const match = trimmed.match(segmentPattern);
      if (!match) continue;

      const quantityRaw = match[1];
      const itemRaw = match[2];
      const unitPriceRaw = match[3];
      const isTotalSegment = /\b(total|in\s+total|for\s+a\s+total\s+of)\b/i.test(trimmed);

      let item = (itemRaw || '').trim();
      if (/\d/.test(item)) continue;
      item = item.replace(/^(a|an|the)\s+/i, '');
      item = item.replace(/\s+(at|for)$/i, '');
      item = item.replace(/\s+(each|per)$/i, '');
      if (isTotalSegment) {
        item = item.replace(/\b(total|in\s+total|for\s+a\s+total\s+of)\b/gi, '');
      }
      item = item.replace(/\s{2,}/g, ' ').trim();
      if (/[\u20ac\u00a3$]/.test(item)) continue;
      if (item.length < 2 || !/[a-z]/i.test(item)) continue;

      const quantity = isNaN(parseFloat(quantityRaw)) ? wordToNumber(quantityRaw) : parseFloat(quantityRaw);
      const unitPrice = parseNumeric(unitPriceRaw);
      if (isNaN(quantity) || isNaN(unitPrice) || quantity <= 0 || unitPrice <= 0) continue;

      const index = textWithoutDates.indexOf(trimmed, searchStart);
      const effectiveIndex = index === -1 ? searchStart : index;
      if (index !== -1) searchStart = index + trimmed.length;

      if (isTotalSegment) {
        const totalValue = unitPrice;
        const computedUnitPrice = totalValue / quantity;
        if (isNaN(computedUnitPrice) || computedUnitPrice <= 0) continue;
        addResult(quantity, computedUnitPrice, item, effectiveIndex, trimmed, totalValue, '');
      } else {
        addResult(quantity, unitPrice, item, effectiveIndex, trimmed, null, '');
      }
    }

    return results
      .sort((a, b) => a.index - b.index)
      .map(({ index, ...rest }) => ({ ...rest, index }));
  }

  extractQuantityPriceTotal(text) {
    const totals = this.extractQuantityPriceTotals(text);
    return totals.length > 0 ? totals[0] : null;
  }

  // Extract additional item + amount pairs when quantity patterns already exist
  extractAdditionalItemAmounts(text, quantityItems = []) {
    const textWithoutDates = this.removeDatePhrases(text);
    let remaining = textWithoutDates;

    const escapeRegExp = (value) => value.replace(/[.*+?^${}()|[\]\\]/g, '\\$&');

    quantityItems.forEach((item) => {
      if (item?.source) {
        const escapedSource = escapeRegExp(item.source);
        remaining = remaining.replace(new RegExp(escapedSource, 'ig'), (match) => ' '.repeat(match.length));
      }
    });

    remaining = remaining.replace(/\bpaid\s+(?:by|with|in)\s+(cash|card|revolut|paypal|bank|transfer|voucher)\b/gi, ' ');
    remaining = remaining.replace(/\bby\s+(cash|card|revolut|paypal|bank|transfer|voucher)\b/gi, ' ');
    remaining = remaining.replace(/\bwith\s+(cash|card|revolut|paypal|bank|transfer|voucher)\b/gi, ' ');

    remaining = remaining.replace(/\s+/g, ' ').trim();
    if (!remaining) return [];

    const pairs = [];
    const seen = new Set();
    const skipWords = [
      'total', 'subtotal', 'sum', 'grand total', 'each', 'per',
      'for', 'at',
      'euro', 'euros', 'dollar', 'dollars', 'pound', 'pounds',
      'box', 'boxes', 'pack', 'packs', 'bag', 'bags', 'bottle', 'bottles',
      'set', 'sets', 'bundle', 'bundles', 'case', 'cases', 'roll', 'rolls',
      'piece', 'pieces', 'pcs', 'unit', 'units', 'item', 'items'
    ];

    const wordToNumber = (word) => {
      if (!word) return NaN;
      const map = {
        one: 1, two: 2, three: 3, four: 4, five: 5, six: 6, seven: 7, eight: 8, nine: 9, ten: 10,
        eleven: 11, twelve: 12, thirteen: 13, fourteen: 14, fifteen: 15, sixteen: 16,
        seventeen: 17, eighteen: 18, nineteen: 19, twenty: 20, thirty: 30, forty: 40, fifty: 50
      };
      return map[word.toLowerCase()] ?? NaN;
    };

    const parseAmountValue = (value) => {
      if (!value) return NaN;
      const normalized = value.toString().trim().toLowerCase();

      const directWord = wordToNumber(normalized);
      if (!isNaN(directWord)) return directWord;

      const wordParts = normalized
        .replace(/[^a-z\s-]/g, ' ')
        .trim()
        .split(/[\s-]+/)
        .filter(Boolean);

      if (wordParts.length === 2) {
        const tens = wordToNumber(wordParts[0]);
        const ones = wordToNumber(wordParts[1]);
        if (!isNaN(tens) && !isNaN(ones) && tens >= 20 && ones < 10) {
          return tens + ones;
        }
      }

      const cleaned = normalized.replace(/[^\d.]/g, '');
      if (!cleaned) return NaN;
      return parseFloat(cleaned);
    };

    const numberWord = '(?:\\d+(?:\\.\\d{1,2})?|one|two|three|four|five|six|seven|eight|nine|ten|eleven|twelve|thirteen|fourteen|fifteen|sixteen|seventeen|eighteen|nineteen|twenty|thirty|forty|fifty)';
    const packTypes = '(?:box|boxes|pack|packs|bag|bags|bottle|bottles|set|sets|bundle|bundles|case|cases|roll|rolls)';
    const itemAmountRegex = new RegExp(
      `([a-z][a-z\\s]+?)\\s+(?:for|at)?\\s*([\\u20ac\\u00a3\\$]?(?:${numberWord}))\\s*(?:euro|euros?|dollars?|pounds?)?(?=\\s+[a-z]|$)`,
      'gi'
    );
    const packOfItemAmountRegex = new RegExp(
      `(?:a|an|one)?\\s*${packTypes}\\s+of\\s+([a-z][a-z\\s]+?)(?=\\s+\\d)\\s+\\d+[\\s\\S]*?(?:total\\s+cost|total|in\\s+total|for\\s+a\\s+total\\s+of)?\\s*([\\u20ac\\u00a3\\$]?(?:${numberWord}))\\s*(?:euro|euros?|dollars?|pounds?)?`,
      'gi'
    );

    const detectPackInfo = (segment) => {
      if (!segment) return null;
      const text = segment.toLowerCase();
      const patterns = [
        /(\d+)\s*(?:items?|pieces?|pcs|units?|count|ct)\s*(?:in|per)\s+(?:the\s+)?(box|pack|bag|bottle|set|bundle|case|roll)/i,
        /(\d+)\s+[a-zA-Z\s]+?\s+(?:in|per)\s+(?:the\s+)?(box|pack|bag|bottle|set|bundle|case|roll)/i,
        /(\d+)\s*(?:items?|pieces?|pcs|units?|count|ct)\s*(?:in\s+(?:a\s+)?)?(box|pack|bag|bottle|set|bundle|case|roll)/i,
        /(box|pack|bag|bottle|set|bundle|case|roll)\s+of\s+(\d+)/i,
        /(\d+)\s*(pack|box|bag|bottle|set|bundle|case|roll)\b/i,
        /(\d+)\s*(?:items?|pieces?|pcs|units?)\b/i
      ];
      for (const pattern of patterns) {
        const match = text.match(pattern);
        if (!match) continue;
        if (pattern === patterns[0]) {
          return { unitsPerPack: parseInt(match[1], 10), packType: match[2] };
        }
        if (pattern === patterns[1]) {
          return { unitsPerPack: parseInt(match[1], 10), packType: match[2] };
        }
        if (pattern === patterns[2]) {
          return { unitsPerPack: parseInt(match[1], 10), packType: match[2] };
        }
        if (pattern === patterns[3]) {
          return { unitsPerPack: parseInt(match[2], 10), packType: match[1] };
        }
        return { unitsPerPack: parseInt(match[1], 10), packType: match[2] };
      }
      return null;
    };

    const addPair = (itemName, amount, index, packInfo) => {
      if (!itemName || isNaN(amount) || amount <= 0) return;

      let cleanItem = this.resolveModelItemName(itemName, remaining, index);
      if (this.isLikelyModelNumberAmount(cleanItem, amount, remaining, index)) return;
      if (/\d/.test(cleanItem) && !this.isKnownModelItemName(cleanItem)) return;

      cleanItem = cleanItem.replace(/^(and|then|bought|purchase|purchased|buy)\s+/i, '');
      cleanItem = cleanItem.replace(/^(a|an|the)\s+/i, '');
      cleanItem = cleanItem.replace(/^of\s+/i, '');
      cleanItem = cleanItem.replace(/^(?:a\s+|an\s+|the\s+)?(box|pack|bag|bottle|set|bundle|case|roll)s?\s+of\s+/i, '');
      cleanItem = cleanItem.replace(/\s+(?:in|per)\s+(?:the\s+)?(box|pack|bag|bottle|set|bundle|case|roll)\b.*$/i, '');
      cleanItem = cleanItem.replace(/\btotal\s+cost\b/gi, '');
      cleanItem = cleanItem.replace(/\btotal\b/gi, '');
      cleanItem = cleanItem.replace(/\b(cost)\b/gi, '');
      cleanItem = cleanItem.replace(/\s+(at|for)$/i, '');
      cleanItem = cleanItem.replace(/\s{2,}/g, ' ').trim();

      if (cleanItem.length < 3 || !/[a-z]/i.test(cleanItem)) return;
      const lowerItem = cleanItem.toLowerCase();
      const ignoreTokens = new Set(['of', 'in', 'per', 'the', 'a', 'an']);
      const tokens = lowerItem.split(/\s+/).filter(Boolean);
      const meaningfulTokens = tokens.filter(token => !skipWords.includes(token) && !ignoreTokens.has(token));
      if (meaningfulTokens.length === 0) return;

      const key = `${lowerItem}|${amount}`;
      if (seen.has(key)) return;
      seen.add(key);

      const unitsPerPack = packInfo?.unitsPerPack && packInfo.unitsPerPack > 0
        ? packInfo.unitsPerPack
        : null;
      pairs.push({
        item: cleanItem,
        amount,
        index,
        unitsPerPack,
        packType: packInfo?.packType || ''
      });
    };

    const matches = [];
    let packMatch;
    while ((packMatch = packOfItemAmountRegex.exec(remaining)) !== null) {
      const itemName = packMatch[1];
      const rawAmount = packMatch[2];
      const amount = parseAmountValue(rawAmount);
      if (isNaN(amount) || amount <= 0) continue;
      matches.push({
        itemName,
        amount,
        rawAmount,
        index: packMatch.index,
        length: packMatch[0].length,
        source: packMatch[0]
      });
    }
    let match;
    while ((match = itemAmountRegex.exec(remaining)) !== null) {
      const itemName = match[1];
      const rawAmount = match[2];
      const amount = parseAmountValue(rawAmount);
      if (isNaN(amount) || amount <= 0) continue;
      const loweredName = (itemName || '').trim().toLowerCase();
      if (skipWords.some(word => loweredName === word || loweredName.includes(word))) {
        continue;
      }
      matches.push({
        itemName,
        amount,
        rawAmount,
        index: match.index,
        length: match[0].length,
        source: match[0]
      });
    }

    matches.forEach((entry, idx) => {
      const nextIndex = idx < matches.length - 1 ? matches[idx + 1].index : remaining.length;
      const tail = remaining.slice(entry.index + entry.length, nextIndex);
      const rawAmountText = entry.rawAmount ? entry.rawAmount.toString() : '';
      const amountIsPlainNumber = rawAmountText && /^[0-9]+$/.test(rawAmountText.replace(/[^\d]/g, '')) && !/[€£$]/.test(rawAmountText);
      const tailHasPackHint = /\b(pack|box|bag|bottle|set|bundle|case|roll|pieces?|pcs|items?|units?)\b/i.test(tail);
      const tailHasPrice = /[€£$]\s*\d+|\d+\s*(?:euro|euros?|dollars?|pounds?)/i.test(tail);
      if (amountIsPlainNumber && tailHasPackHint && tailHasPrice) {
        return;
      }
      let packInfo = detectPackInfo(tail);
      if (!packInfo && entry.source) {
        packInfo = detectPackInfo(entry.source);
      }
      let amount = entry.amount;
      if (!packInfo && entry.rawAmount) {
        const tailPackMatch = tail.match(/\b(pieces?|pcs|items?|units?)\b/i);
        const raw = entry.rawAmount.toString().trim().replace(/[€£$]/g, '');
        const decimalMatch = raw.match(/^(\d+)[.,](\d{2})$/);
        if (tailPackMatch && decimalMatch) {
          const packPrice = parseInt(decimalMatch[1], 10);
          const unitsPerPack = parseInt(decimalMatch[2], 10);
          if (!isNaN(packPrice) && packPrice > 0 && !isNaN(unitsPerPack) && unitsPerPack > 1) {
            amount = packPrice;
            packInfo = {
              unitsPerPack,
              packType: tailPackMatch[1].toLowerCase()
            };
          }
        }
      }
      addPair(entry.itemName, amount, entry.index, packInfo);
    });

    return pairs;
  }

  // Extract a date from free text (returns ISO yyyy-mm-dd or null)
  extractDate(text) {
    const now = new Date();
    const monthMap = {
      january: 1, february: 2, march: 3, april: 4, may: 5, june: 6, july: 7,
      august: 8, september: 9, sept: 9, october: 10, november: 11, december: 12,
      jan: 1, feb: 2, mar: 3, apr: 4, jun: 6, jul: 7, aug: 8, sep: 9, oct: 10, nov: 11, dec: 12
    };

    // Relative dates
    if (/yesterday/i.test(text)) {
      const d = new Date(now);
      d.setDate(d.getDate() - 1);
      return d.toISOString().split('T')[0];
    }
    if (/today/i.test(text)) {
      return now.toISOString().split('T')[0];
    }

    const patterns = [
      /\b(\d{1,2})(st|nd|rd|th)?\s+(january|february|march|april|may|june|july|august|september|sept|october|november|december|jan|feb|mar|apr|jun|jul|aug|sep|oct|nov|dec)(?:\s+(\d{4}))?\b/i,
      /\b(january|february|march|april|may|june|july|august|september|sept|october|november|december|jan|feb|mar|apr|jun|jul|aug|sep|oct|nov|dec)\s+(\d{1,2})(st|nd|rd|th)?(?:\s+(\d{4}))?\b/i
    ];

    for (const pattern of patterns) {
      const match = text.match(pattern);
      if (match) {
        let day;
        let month;
        let year = match[4] ? parseInt(match[4], 10) : now.getFullYear();

        if (pattern === patterns[0]) {
          day = parseInt(match[1], 10);
          month = monthMap[match[3].toLowerCase()];
        } else {
          day = parseInt(match[2], 10);
          month = monthMap[match[1].toLowerCase()];
        }

        if (!isNaN(day) && month) {
          const parsedDate = new Date(year, month - 1, day);
          if (!isNaN(parsedDate.getTime())) {
            return parsedDate.toISOString().split('T')[0];
          }
        }
      }
    }

    return null;
  }

  // Detect split payments and create separate transactions
  detectSplitPayments(transcript) {
    const text = transcript.toLowerCase();
    const textWithoutDates = this.removeDatePhrases(text);
    
    // Skip split payment detection if this contains VAT information
    const vatPatterns = [
      /\d+(?:\.\d{2})?\s*(?:euro|euros?)?\s+net.*?vat/i,
      /\d+(?:\.\d{1,2})?\s*(?:euro|euros?|eur|dollars?|pounds?)?\s+net\s+\d+(?:\.\d{1,2})?\s*vat/i,
      /\d+(?:\.\d{1,2})?\s*(?:euro|euros?|eur|dollars?|pounds?)?\s+vat\s+\d+(?:\.\d{1,2})?/i,
      /net.*?\d+(?:\.\d{2})?\s*(?:euro|euros?)?\s+vat/i,
      /\d+(?:\.\d{2})?\s*%\s*vat/i,
      /vat\s+\d+(?:\.\d{2})?\s*%/i,
      /net.*?vat.*?\d+(?:\.\d{2})?/i
    ];
    
    for (const pattern of vatPatterns) {
      if (pattern.test(text)) {
        console.log('TransactionParser: VAT pattern detected in split payment check, skipping split detection');
        return [];
      }
    }
    
    // Payment method patterns with their keywords
    const paymentMethods = {
      cash: ['cash', 'in cash'],
      card: ['card', 'by card', 'with card', 'debit', 'credit'],
      revolut: ['revolut', 'by revolut'],
      paypal: ['paypal', 'by paypal'],
      bank: ['bank transfer', 'transfer', 'bank']
    };
    
    // Find all amount-payment pairs
    const amountPaymentPairs = [];
    
    // Look for patterns like "€30 cash", "50 euro card", "€60 by card", "thirty euros cash", etc.
    // Use a comprehensive pattern to catch various speech formats
    const patterns = [
      // Pattern 1: €30 cash, 50 euro card, €60 by card
      /(?:([€$£])(\d+(?:\.\d{2})?)|(\d+(?:\.\d{2})?)(?:\s*([€$£])|(?:\s+(euros?|dollars?|pounds?))))\s+(?:by\s+|paid\s+(?:by\s+|in\s+|with\s+))?(cash|card|revolut|paypal|bank)/gi,
      // Pattern 2: thirty euros cash, fifty dollars card (for speech recognition numbers)
      /(twenty|thirty|forty|fifty|sixty|seventy|eighty|ninety|one hundred|\d+)\s+(euros?|dollars?|pounds?)\s+(?:by\s+|paid\s+(?:by\s+|in\s+|with\s+))?(cash|card|revolut|paypal|bank)/gi
    ];
    
    // Helper function to convert word numbers to digits
    const wordToNumber = (word) => {
      if (!word) return NaN;
      const numbers = {
        'twenty': 20, 'thirty': 30, 'forty': 40, 'fifty': 50,
        'sixty': 60, 'seventy': 70, 'eighty': 80, 'ninety': 90,
        'one hundred': 100
      };
      return numbers[word.toLowerCase()] || parseFloat(word);
    };

    // Process each pattern separately to avoid confusion
    patterns.forEach((pattern, patternIndex) => {
      let match;
      while ((match = pattern.exec(textWithoutDates)) !== null) {
        let amount;
        let paymentMethod;
        

        
        if (patternIndex === 0) {
          // Pattern 1: €30 cash, 50 euro card, €60 by card
          if (match[1] && match[2]) {
            // €30 cash
            amount = parseFloat(match[2]);
            paymentMethod = this.normalizePaymentMethod(match[6]);
          } else if (match[3]) {
            // 30 euro cash or 30 cash
            amount = parseFloat(match[3]);
            paymentMethod = this.normalizePaymentMethod(match[6]);
          }
        } else if (patternIndex === 1) {
          // Pattern 2: thirty euros cash, fifty dollars card
          amount = wordToNumber(match[1]);
          paymentMethod = this.normalizePaymentMethod(match[3]);
        }
        
        if (!isNaN(amount) && paymentMethod) {
          // Check if we already have this exact combination
          const exists = amountPaymentPairs.some(pair => 
            pair.amount === amount && 
            pair.paymentMethod === paymentMethod &&
            Math.abs(match.index - text.indexOf(pair.originalText)) < 20
          );
          
          if (!exists) {
            amountPaymentPairs.push({
              amount: amount,
              paymentMethod: paymentMethod,
              originalText: match[0]
            });
          }
        }
      }
    });
    
    console.log('TransactionParser: Found amount-payment pairs:', amountPaymentPairs);
    
    // If we found multiple payment methods, create separate transactions
    if (amountPaymentPairs.length > 1) {
      const transactions = [];
      const transactionType = this.detectTransactionType(text);
      const baseCategory = transactionType === 'income' 
        ? this.categorizeIncomeTransaction(text)
        : this.categorizeExpenseTransaction(text, this.extractItems(text));
      
      for (const pair of amountPaymentPairs) {
        // Create description by removing other amounts and payment methods
        let description = transcript;
        for (const otherPair of amountPaymentPairs) {
          if (otherPair !== pair) {
            description = description.replace(new RegExp(otherPair.originalText, 'gi'), '');
          }
        }
        description = this.extractDescription(description);
        
        transactions.push({
          type: transactionType,
          amount: pair.amount,
          description: description,
          category: baseCategory,
          paymentMethod: pair.paymentMethod,
          date: this.extractDate(text),
          items: this.extractItems(text),
          confidence: 80,
          splitPaymentInfo: {
            isPartOfSplit: true,
            totalAmount: amountPaymentPairs.reduce((sum, p) => sum + p.amount, 0),
            splitCount: amountPaymentPairs.length
          }
        });
      }
      
      return transactions;
    }
    
    // No split payment detected
    return [];
  }

  // Normalize payment method names
  normalizePaymentMethod(method) {
    const normalized = method.toLowerCase().trim();
    if (['cash', 'in cash'].includes(normalized)) return 'cash';
    if (['card', 'by card', 'with card', 'debit', 'credit'].includes(normalized)) return 'card';
    if (['revolut', 'by revolut'].includes(normalized)) return 'revolut';
    if (['paypal', 'by paypal'].includes(normalized)) return 'paypal';
    if (['bank transfer', 'transfer', 'bank'].includes(normalized)) return 'bank';
    return normalized;
  }

  // Detect split categories (different items with different categories and amounts)
  detectSplitCategories(transcript) {
    const text = transcript.toLowerCase();
    const textWithoutDates = this.removeDatePhrases(text);

    // If we have a clear quantity x unit price pattern, treat as single transaction
    if (this.extractQuantityPriceTotal(text)) {
      return [];
    }
    
    // Skip split detection if this contains VAT information
    const vatPatterns = [
      /\d+(?:\.\d{1,2})?\s*(?:euro|euros?|eur|dollars?|pounds?)?\s+net\s+\d+(?:\.\d{1,2})?\s*vat/i,
      /\d+(?:\.\d{1,2})?\s*(?:euro|euros?|eur|dollars?|pounds?)?\s+vat\s+\d+(?:\.\d{1,2})?/i,
      /net\s+\d+(?:\.\d{2})?\s*(?:euro|euros?)?\s+vat\s+\d+(?:\.\d{2})?/i,
      /\d+(?:\.\d{2})?\s*(?:euro|euros?)?\s+net\s+vat\s+\d+(?:\.\d{2})?/i,
      /net\s+amount\s+\d+(?:\.\d{2})?\s+vat\s+amount\s+\d+(?:\.\d{2})?/i,
      /\d+(?:\.\d{2})?\s+net\s+\d+(?:\.\d{2})?\s+vat/i
    ];
    
    for (const pattern of vatPatterns) {
      if (pattern.test(text)) {
        console.log('TransactionParser: VAT pattern detected, skipping split detection');
        return [];
      }
    }
    
    // Process both expense and income transactions
    const transactionType = this.detectTransactionType(text);
    
    // Look for patterns like "hairdryer for 200 euro and shampoo for 30 euro"
    // Use a more precise approach to avoid duplicates
    const itemAmountPairs = [];
    
    // Special case 1: Handle mixed formats like "nail polish €20 hair dryer 200"
    const mixedFormatPattern = /([a-zA-Z\s]+?)\s+([€$£]\d+(?:\.\d{2})?)\s+([a-zA-Z\s]+?)\s+(\d+(?:\.\d{2})?)/;
    const mixedMatch = textWithoutDates.match(mixedFormatPattern);
    if (mixedMatch) {
      const item1 = mixedMatch[1].replace(/^(bought|purchase|purchased|buy)\s+/i, '').trim();
      const amount1 = parseFloat(mixedMatch[2].replace(/[€$£]/g, ''));
      const item2 = mixedMatch[3].trim();
      const amount2 = parseFloat(mixedMatch[4]);
      
      if (item1.length >= 3 && item2.length >= 3 && !isNaN(amount1) && !isNaN(amount2)) {
        itemAmountPairs.push(
          { item: item1, amount: amount1, originalText: `${item1} ${mixedMatch[2]}` },
          { item: item2, amount: amount2, originalText: `${item2} ${mixedMatch[4]}` }
        );
      }
    }
    
    // Special case 2: Handle formats like "bought hair dryer 200 nail polish 20" or "haircut 40 acrylic nails 50"
    if (itemAmountPairs.length === 0) {
      // More flexible pattern that handles multi-word items better
      const numbersOnlyPattern = /^(.+?)\s+(\d+(?:\.\d{2})?)\s+(.+?)\s+(\d+(?:\.\d{2})?)$/;
      const numbersMatch = textWithoutDates.match(numbersOnlyPattern);
      if (numbersMatch) {
        // Remove purchase keywords if present (for expenses), but keep original for sales
        const item1 = numbersMatch[1].replace(/^(bought|purchase|purchased|buy)\s+/i, '').trim();
        const amount1 = parseFloat(numbersMatch[2]);
        const item2 = numbersMatch[3].trim();
        const amount2 = parseFloat(numbersMatch[4]);
        

        
        // Ensure both items are meaningful (at least 3 characters and not just numbers)
        if (item1.length >= 3 && item2.length >= 3 && !isNaN(amount1) && !isNaN(amount2) && 
            !/^\d+$/.test(item1.trim()) && !/^\d+$/.test(item2.trim())) {
          itemAmountPairs.push(
            { item: item1, amount: amount1, originalText: `${item1} ${numbersMatch[2]}` },
            { item: item2, amount: amount2, originalText: `${item2} ${numbersMatch[4]}` }
          );
        }
      }
    }
    
    // If mixed format didn't work, try standard global patterns
    let foundGlobalMatches = false;
    if (itemAmountPairs.length === 0) {
      const globalPatterns = [
        // Pattern: "item €amount" or "item $amount" 
        /([a-zA-Z\s]+?)\s+([€$£]\d+(?:\.\d{2})?)/g,
        // Pattern: "item amount euro/dollar"
        /([a-zA-Z\s]+?)\s+(\d+(?:\.\d{2})?)\s+(?:euros?|dollars?|pounds?)/g,
        // Pattern: "item for amount euro"
        /([a-zA-Z\s]+?)\s+for\s+(\d+(?:\.\d{2})?)\s+(?:euros?|dollars?|pounds?)/g
      ];
    
      for (const pattern of globalPatterns) {
        const matches = [...textWithoutDates.matchAll(pattern)];
        if (matches.length > 1) {
          // Found multiple items with this pattern
          for (const match of matches) {
            let itemName = match[1].trim();
            // Clean up item name
            itemName = itemName.replace(/^(bought|purchase|purchased|buy)\s+/i, '');
            itemName = itemName.replace(/^(a|an|the)\s+/i, '');
            
            const amount = parseFloat(match[2].replace(/[€$£]/g, ''));
            
            if (itemName.length >= 3 && !isNaN(amount)) {
              const exists = itemAmountPairs.some(pair => 
                pair.item.toLowerCase() === itemName.toLowerCase() && pair.amount === amount
              );
              
              if (!exists) {
                itemAmountPairs.push({
                  item: itemName,
                  amount: amount,
                  originalText: match[0]
                });
              }
            }
          }
          foundGlobalMatches = true;
          break; // Found matches with this pattern, no need to try others
        }
      }
    } else {
      foundGlobalMatches = true; // Mixed format worked
    }
    
    // If global patterns didn't work, fall back to segment-based approach
    if (!foundGlobalMatches) {
      // Split by common separators first
      const segments = textWithoutDates.split(/\s+and\s+|\s*,\s*/);
    
    for (const segment of segments) {
      // Clean up the segment
      let cleanSegment = segment.trim();
      
      // Remove common prefixes
      cleanSegment = cleanSegment.replace(/^(bought|purchase|purchased|buy)\s+/i, '');
      cleanSegment = cleanSegment.replace(/^(a|an|the)\s+/i, '');
      
      // Look for item-amount patterns in this segment
      const patterns = [
        // Pattern 1: "hairdryer for 200 euro"
        /^(.+?)\s+for\s+([€$£]?\d+(?:\.\d{2})?)\s*(?:euros?|dollars?|pounds?)?$/i,
        // Pattern 2: "hairdryer €200"
        /^(.+?)\s+([€$£]\d+(?:\.\d{2})?)$/i,
        // Pattern 3: "hairdryer 200 euro"
        /^(.+?)\s+(\d+(?:\.\d{2})?)\s+(?:euros?|dollars?|pounds?)$/i
      ];
      
      for (const pattern of patterns) {
        const match = cleanSegment.match(pattern);
        if (match) {
          const itemName = match[1].trim();
          const amount = parseFloat(match[2].replace(/[€$£]/g, ''));
          
          // Skip if item name is too short or amount is invalid
          if (itemName.length < 3 || isNaN(amount)) continue;
          
          // Skip common words that aren't items
          const skipWords = ['and', 'for', 'with', 'the', 'a', 'an'];
          if (skipWords.includes(itemName.toLowerCase())) continue;
          
          // Check if we already have this item (avoid duplicates)
          const exists = itemAmountPairs.some(pair => 
            pair.item.toLowerCase() === itemName.toLowerCase() && pair.amount === amount
          );
          
          if (!exists) {
            itemAmountPairs.push({
              item: itemName,
              amount: amount,
              originalText: match[0]
            });
          }
          break; // Found a match, no need to try other patterns
        }
      }
    }
    } // Close the segment-based approach
    

    
    // If we found multiple items with amounts, categorize each
    if (itemAmountPairs.length > 1) {
      const transactions = [];
      const paymentMethod = this.detectPaymentMethod(text);
      
      for (const pair of itemAmountPairs) {
        const category = transactionType === 'income'
          ? this.categorizeIncomeTransaction(pair.item)
          : this.categorizeExpenseTransaction(pair.item, [pair.item]);
        
        transactions.push({
          type: transactionType,
          amount: pair.amount,
          description: `${pair.item}`,
          category: category,
          paymentMethod: paymentMethod,
          date: this.extractDate(text),
          items: [pair.item],
          confidence: 85,
          splitCategoryInfo: {
            isPartOfSplit: true,
            totalAmount: itemAmountPairs.reduce((sum, p) => sum + p.amount, 0),
            splitCount: itemAmountPairs.length,
            splitType: 'category'
          }
        });
      }
      
      return transactions;
    }
    
    // No split categories detected
    return [];
  }

  // Categorize a specific item (more precise than general categorization)
  categorizeSpecificItem(itemName) {
    const item = itemName.toLowerCase().trim();
    
    // Check each category for this specific item
    let bestCategory = 'misc';
    let highestScore = 0;
    
    for (const [category, keywords] of Object.entries(this.categoryKeywords)) {

      const score = keywords.reduce((acc, keyword) => {
        // Exact match gets higher score
        if (item === keyword) return acc + 3;
        // Contains match gets normal score
        if (item.includes(keyword)) return acc + 1;
        return acc;
      }, 0);

      if (score > highestScore) {
        highestScore = score;
        bestCategory = category;
      }
    }
    

    return bestCategory;
  }

  // Extract VAT information from text
  extractVATInfo(text) {
    // Patterns to match VAT expressions:
    // "net 20 euro vat 4.6"
    // "net 20 vat 4.6"  
    // "20 euro net vat 4.6"
    // "net amount 20 vat amount 4.6"
    // "20 net 4.6 vat"
    
    const vatPatterns = [
      // Pattern 1: "net X vat Y" or "net X euro vat Y"
      /net\s+(\d+(?:\.\d{2})?)\s*(?:euro|euros?)?\s+vat\s+(\d+(?:\.\d{2})?)/i,
      // Pattern 2: "X euro net vat Y" 
      /(\d+(?:\.\d{2})?)\s*(?:euro|euros?)?\s+net\s+vat\s+(\d+(?:\.\d{2})?)/i,
      // Pattern 3: "net amount X vat amount Y"
      /net\s+amount\s+(\d+(?:\.\d{2})?)\s+vat\s+amount\s+(\d+(?:\.\d{2})?)/i,
      // Pattern 4: "300 euro net 69 vat" or "300 net 69 vat"
      /(\d+(?:\.\d{1,2})?)\s*(?:euro|euros?|eur|dollars?|pounds?)?\s+net\s+(\d+(?:\.\d{1,2})?)\s*vat/i,
      // Pattern 4: "X net Y vat"
      /(\d+(?:\.\d{2})?)\s+net\s+(\d+(?:\.\d{2})?)\s+vat/i,
      // Pattern 5: "X euro net, Y% vat" - handle percentage with net amount
      /(\d+(?:\.\d{2})?)\s*(?:euro|euros?)?\s+net.*?(\d+(?:\.\d{1,2})?)\s*%\s*vat/i
    ];

    for (const pattern of vatPatterns) {
      const match = text.match(pattern);
      if (match) {
        const netAmount = parseFloat(match[1]);
        const secondValue = parseFloat(match[2]);
        
        if (!isNaN(netAmount) && !isNaN(secondValue) && netAmount > 0 && secondValue > 0) {
          // Check if this is the percentage pattern (Pattern 5)
          if (pattern.source.includes('%')) {
            // Second value is a percentage rate
            const vatRate = secondValue / 100;
            const vatAmount = netAmount * vatRate;
            
            return {
              netAmount: netAmount,
              vatAmount: parseFloat(vatAmount.toFixed(2)),
              vatRate: parseFloat(vatRate.toFixed(4)),
              grossAmount: parseFloat((netAmount + vatAmount).toFixed(2))
            };
          } else {
            // Second value is VAT amount
            const vatAmount = secondValue;
            const vatRate = vatAmount / netAmount;
            
            return {
              netAmount: netAmount,
              vatAmount: vatAmount,
              vatRate: parseFloat(vatRate.toFixed(4)), // Keep 4 decimal places for precision
              grossAmount: netAmount + vatAmount
            };
          }
        }
      }
    }

    // Also check for explicit VAT rate patterns like "20% vat" or "vat 20%"
    const vatRatePatterns = [
      /(\d+(?:\.\d{1,2})?)\s*%\s*vat/i,
      /vat\s+(\d+(?:\.\d{1,2})?)\s*%/i
    ];

    for (const pattern of vatRatePatterns) {
      const match = text.match(pattern);
      if (match) {
        const vatRatePercent = parseFloat(match[1]);
        if (!isNaN(vatRatePercent) && vatRatePercent > 0) {
          return {
            netAmount: null,
            vatAmount: null,
            vatRate: vatRatePercent / 100, // Convert percentage to decimal
            grossAmount: null
          };
        }
      }
    }

    return null;
  }

  // Main parsing method
  parseTransaction(transcript) {
    const text = transcript.toLowerCase();
    const quantityItems = this.extractQuantityPriceTotals(text);
    const extraItems = this.extractAdditionalItemAmounts(text, quantityItems);
    const quantityInfo = quantityItems.length === 1 ? quantityItems[0] : null;

    const formatPackLabel = (units, packType) => {
      if (!units) return '';
      const rawType = (packType || 'units').toLowerCase().trim();
      const type = rawType === 'packs' ? 'pack' : rawType;
      return `${units} ${type}`;
    };

    const buildLineItem = (item) => {
      if (!item) return null;
      const baseDescription = item.item || item.description || item.name || 'Voice input item';
      const amount = Number(item.amount) || 0;
      const unitsPerPack = item.unitsPerPack && Number(item.unitsPerPack) > 0 ? Number(item.unitsPerPack) : null;
      const packPrice = unitsPerPack ? amount : null;
      const unitPrice = unitsPerPack ? amount / unitsPerPack : amount;
      const displayDescription = unitsPerPack
        ? `${baseDescription} (${formatPackLabel(unitsPerPack, item.packType)})`
        : baseDescription;
      return {
        description: displayDescription,
        baseDescription,
        amount,
        unitPrice: parseFloat(unitPrice.toFixed(4)),
        packPrice,
        unitsPerPack,
        packType: item.packType || '',
        saveToCost: true
      };
    };
    
    // FIRST: Check if this contains VAT information - if so, treat as single transaction
    const vatPatterns = [
      // Pattern for "diesel €20 net 23% VAT"
      /\w+\s+[€$£]?\d+(?:\.\d{2})?\s*(?:euro|euros?)?\s+net\s+\d+(?:\.\d{2})?\s*%\s+vat/i,
      // Pattern for "20 euro net 23% vat"
      /\d+(?:\.\d{2})?\s*(?:euro|euros?)?\s+net\s+\d+(?:\.\d{2})?\s*%\s+vat/i,
      // Pattern for "salon tools 300 euro net 69 vat" or "300 net 69 vat"
      /\d+(?:\.\d{1,2})?\s*(?:euro|euros?|eur|dollars?|pounds?)?\s+net\s+\d+(?:\.\d{1,2})?\s*vat/i,
      // Pattern for "20 euro vat 4.60"
      /\d+(?:\.\d{1,2})?\s*(?:euro|euros?|eur|dollars?|pounds?)?\s+vat\s+\d+(?:\.\d{1,2})?/i,
      // Pattern for "net 20 euro vat 4.60"
      /net\s+\d+(?:\.\d{2})?\s*(?:euro|euros?)?\s+vat\s+\d+(?:\.\d{2})?/i,
      // Pattern for "20 euro net vat 4.60"
      /\d+(?:\.\d{2})?\s*(?:euro|euros?)?\s+net\s+vat\s+\d+(?:\.\d{2})?/i,
      // Pattern for "net amount 20 vat amount 4.60"
      /net\s+amount\s+\d+(?:\.\d{2})?\s+vat\s+amount\s+\d+(?:\.\d{2})?/i,
      // Pattern for "20 net 4.60 vat"
      /\d+(?:\.\d{2})?\s+net\s+\d+(?:\.\d{2})?\s*%?\s+vat/i,
      // Pattern for any item with "net" and "vat" keywords
      /\w+.*net.*\d+.*%.*vat|\w+.*net.*vat.*\d+/i
    ];
    
    const hasVAT = vatPatterns.some(pattern => pattern.test(text));
    if (hasVAT) {
      console.log('TransactionParser: VAT detected, treating as single transaction');
      // Skip split detection and proceed to single transaction parsing
    } else {
      const combinedItemsWithIndex = [
        ...quantityItems.map(item => ({ item: item.item, amount: item.total, index: item.index, unitsPerPack: item.unitsPerPack, packType: item.packType })),
        ...extraItems
      ];

      const combinedItems = combinedItemsWithIndex
        .sort((a, b) => (a.index ?? Number.MAX_SAFE_INTEGER) - (b.index ?? Number.MAX_SAFE_INTEGER))
        .map(({ item, amount }) => ({ item, amount }));

      const combinedLineItems = combinedItemsWithIndex
        .sort((a, b) => (a.index ?? Number.MAX_SAFE_INTEGER) - (b.index ?? Number.MAX_SAFE_INTEGER))
        .map((item) => buildLineItem(item))
        .filter(Boolean);

      if (combinedItems.length > 1) {
        const transactionType = this.detectTransactionType(text);
        const paymentMethod = this.detectPaymentMethod(text);
        const date = this.extractDate(text);
        const totalAmount = combinedItems.reduce((sum, item) => sum + item.amount, 0);

        const transactions = combinedItems.map((item, idx) => {
          const lineItem = combinedLineItems[idx] || null;
          const baseDescription = lineItem?.baseDescription || item.item || 'Voice input item';
          const description = lineItem?.description || baseDescription;
          const category = transactionType === 'income'
            ? this.categorizeIncomeTransaction(baseDescription.toLowerCase())
            : this.categorizeExpenseTransaction(baseDescription.toLowerCase(), [baseDescription]);

          return {
            type: transactionType,
            amount: item.amount,
            description,
            category,
            paymentMethod,
            date,
            items: lineItem ? [lineItem] : (description ? [description] : []),
            confidence: 85,
            splitCategoryInfo: {
              isPartOfSplit: true,
              totalAmount,
              splitCount: combinedItems.length,
              splitType: 'category'
            }
          };
        });

        console.log('TransactionParser: Detected quantity-based split, returning multiple transactions:', transactions.length);
        return {
          isSplitPayment: true,
          transactions
        };
      }

      // Check if this is a split payment transaction
      const splitPaymentTransactions = quantityItems.length ? [] : this.detectSplitPayments(transcript);
      if (splitPaymentTransactions.length > 1) {
        console.log('TransactionParser: Detected split payment, returning multiple transactions:', splitPaymentTransactions.length);
        return {
          isSplitPayment: true,
          transactions: splitPaymentTransactions
        };
      }
      
      // Check if this is a split category transaction (different items, different categories)
      const splitCategoryTransactions = quantityItems.length ? [] : this.detectSplitCategories(transcript);
      if (splitCategoryTransactions.length > 1) {
        console.log('TransactionParser: Detected split categories, returning multiple transactions:', splitCategoryTransactions.length);
        return {
          isSplitPayment: true, // Use same UI structure
          transactions: splitCategoryTransactions
        };
      }
    }
    
    const result = {
      type: this.detectTransactionType(text),
      amount: this.extractAmount(text),
      description: this.extractDescription(transcript), // Use original case for description
      category: null,
      paymentMethod: this.detectPaymentMethod(text),
      items: this.extractItems(text),
      confidence: 0,
      date: this.extractDate(text),
      // VAT information
      vatAmount: null,
      vatRate: null,
      netAmount: null
    };

    const combinedItemsWithIndex = [
      ...quantityItems.map(item => ({ item: item.item, amount: item.total, index: item.index, unitsPerPack: item.unitsPerPack, packType: item.packType })),
      ...extraItems
    ];
    const combinedItems = combinedItemsWithIndex
      .sort((a, b) => (a.index ?? Number.MAX_SAFE_INTEGER) - (b.index ?? Number.MAX_SAFE_INTEGER))
      .map(({ item, amount, unitsPerPack, packType }) => ({ item, amount, unitsPerPack, packType }));

    if (combinedItems.length === 1) {
      const singleItem = combinedItems[0];
      const lineItem = buildLineItem(singleItem);
      result.amount = singleItem.amount || result.amount;
      if (lineItem?.description) {
        result.description = lineItem.description;
      } else {
        result.description = singleItem.item || result.description;
      }
      result.items = lineItem ? [lineItem] : result.items;
    }

    // If we have quantity x price, prefer that total and set a sensible description
    if (quantityInfo && quantityInfo.total > 0) {
      result.amount = quantityInfo.total;
      const cleanedDescription = transcript.replace(/\s+/g, ' ').trim();
      const hasStructuredItems = Array.isArray(result.items) && result.items.length > 0 && typeof result.items[0] === 'object';
      if (!hasStructuredItems) {
        result.description = cleanedDescription || transcript;
      }
      if (quantityInfo.item) {
        if (!hasStructuredItems) {
          result.items = [quantityInfo.item];
        }
        // Use item text to improve category detection based on transaction type
        if (result.type === 'expenses') {
          result.category = this.categorizeExpenseTransaction(quantityInfo.item.toLowerCase(), [quantityInfo.item]);
        } else {
          result.category = this.categorizeIncomeTransaction(quantityInfo.item.toLowerCase());
        }
      }
    }

    // Extract VAT information if present
    const vatInfo = this.extractVATInfo(text);
    if (vatInfo) {
      result.vatAmount = vatInfo.vatAmount;
      result.vatRate = vatInfo.vatRate;
      result.netAmount = vatInfo.netAmount;
      // If we have VAT info, use gross amount (net + VAT) as the main amount
      if (vatInfo.grossAmount !== null) {
        result.amount = vatInfo.grossAmount;
      }
    }

    // Determine category based on type and keywords
    if (!result.category) {
      if (result.type === 'income') {
        result.category = this.categorizeIncomeTransaction(text);
      } else {
        result.category = this.categorizeExpenseTransaction(text, result.items);
      }
    }

    this.applyNonReclaimableFoodVat(result);

    // Calculate confidence score
    result.confidence = this.calculateConfidence(result, text);

    return result;
  }

  // Detect if it's income or expense
  detectTransactionType(text) {
    let incomeScore = 0;
    let expenseScore = 0;

    // Check for income keywords
    this.transactionTypeKeywords.income.forEach(keyword => {
      if (text.includes(keyword)) incomeScore++;
    });

    // Check for expense keywords
    this.transactionTypeKeywords.expense.forEach(keyword => {
      if (text.includes(keyword)) expenseScore++;
    });

    // Default logic: if no clear indicators, assume based on context
    if (incomeScore === expenseScore) {
      const expenseKeywords = Object.entries(this.categoryKeywords)
        .filter(([category]) => !INCOME_CATEGORY_KEYS.includes(category))
        .flatMap(([, keywords]) => keywords);

      const hasExpenseKeywords = expenseKeywords.some(keyword => text.includes(keyword));

      // Look for service-related keywords (likely income)
      const serviceKeywords = [
        ...this.categoryKeywords.nailServices,
        ...this.categoryKeywords.hairServices,
        ...this.categoryKeywords.skinServices,
        ...this.categoryKeywords.injectables,
        ...this.categoryKeywords.bodyTreatments,
        ...this.categoryKeywords.makeupServices,
        ...this.categoryKeywords.massageServices
      ];
      
      const hasServiceKeywords = serviceKeywords.some(keyword => text.includes(keyword));
      if (hasExpenseKeywords) return 'expenses';
      return hasServiceKeywords ? 'income' : 'expenses';
    }

    return incomeScore > expenseScore ? 'income' : 'expenses';
  }

  // Extract monetary amount - prefer currency amounts, handle quantity x price, ignore dates
  extractAmount(text) {
    const normalized = text.toLowerCase();
    const textWithoutDates = this.removeDatePhrases(normalized);

    // Quantity x price
    const quantityInfo = this.extractQuantityPriceTotal(normalized);

    let foundAmounts = [];

    // Check each currency pattern and collect matches (excluding dates)
    for (const pattern of this.currencyPatterns) {
      const matches = [...textWithoutDates.matchAll(pattern)];
      for (const match of matches) {
        const amount = parseFloat(match[1]);
        if (!isNaN(amount)) {
          foundAmounts.push(amount);
        }
      }
    }

    // If quantity detected, prioritise its total when currency matches look like unit prices
    if (quantityInfo && quantityInfo.total > 0) {
      const largestCurrency = foundAmounts.length ? Math.max(...foundAmounts) : 0;
      if (foundAmounts.length === 0 || largestCurrency <= quantityInfo.unitPrice * 1.05) {
        return quantityInfo.total;
      }
      if (quantityInfo.total >= largestCurrency && foundAmounts.length <= 1) {
        return quantityInfo.total;
      }
    }

    if (foundAmounts.length > 0) {
      if (foundAmounts.length === 1) {
        return foundAmounts[0];
      }
      // Multiple amounts with no split detection: choose the largest to avoid double-counting quantities/dates
      return Math.max(...foundAmounts);
    }

    // Fallback: look for standalone numbers (excluding dates and quantity phrases)
    let numberScanText = textWithoutDates;
    if (quantityInfo) {
      numberScanText = numberScanText.replace(/(\d+)\s*[xX]\s*\d+(?:\.\d{1,2})?/g, ' ');
    }
    const numberMatches = [...numberScanText.matchAll(/(\d+(?:\.\d{2})?)/g)];
    const standaloneNumbers = [];
    for (const match of numberMatches) {
      if (this.isNumberPartOfKnownModel(numberScanText, match.index, match[1])) {
        continue;
      }
      const amount = parseFloat(match[1]);
      if (!isNaN(amount)) {
        standaloneNumbers.push(amount);
      }
    }

    if (standaloneNumbers.length > 0) {
      return Math.max(...standaloneNumbers);
    }

    return 0;
  }

  // Extract description (clean version of original transcript)
  extractDescription(transcript) {
    const parsedAmount = this.extractAmount(String(transcript || '').toLowerCase());
    // Remove payment method references and clean up
    let description = transcript
      .replace(/paid (by|in|with) \w+/gi, '')
      .replace(/\bpaid\s+(cash|card|revolut|paypal|bank|transfer|voucher)\b/gi, '')
      .replace(/\bby\s+(cash|card|revolut|paypal|bank|transfer|voucher)\b/gi, '')
      .replace(/\bwith\s+(cash|card|revolut|paypal|bank|transfer|voucher)\b/gi, '')
      .replace(/\b(cash|card|revolut|paypal|bank|transfer|voucher)\b\s*$/gi, '')
      .replace(/\b\d+(?:\.\d{1,2})?\s*(?:euro|euros?|eur|dollars?|pounds?)?\s+net\s+\d+(?:\.\d{1,2})?\s*vat\b/gi, '')
      .replace(/\bnet\s+\d+(?:\.\d{1,2})?\s*(?:euro|euros?|eur|dollars?|pounds?)?\s+vat\s+\d+(?:\.\d{1,2})?\b/gi, '')
      .replace(/\b\d+(?:\.\d{1,2})?\s*(?:euro|euros?|eur|dollars?|pounds?)?\s+vat\s+\d+(?:\.\d{1,2})?\b/gi, '')
      .replace(/€\d+(?:\.\d{2})?/g, '')
      .replace(/\$\d+(?:\.\d{2})?/g, '')
      .replace(/£\d+(?:\.\d{2})?/g, '')
      .replace(/\d+ euros?/gi, '')
      .replace(/\d+ dollars?/gi, '')
      .replace(/\d+ pounds?/gi, '')
      .replace(/\s+/g, ' ')
      .trim();

    if (parsedAmount > 0) {
      const amountText = String(parsedAmount).replace(/\.0+$/, '');
      const trailingAmountPattern = new RegExp(`\\b${this.escapeRegExp(amountText)}(?:\\.00)?\\b\\s*$`, 'i');
      description = description.replace(trailingAmountPattern, (match, offset) => (
        this.isNumberPartOfKnownModel(description, offset, amountText) ? match : ''
      )).replace(/\s+/g, ' ').trim();
    }

    description = description
      .replace(/\b(net|vat)\b/gi, '')
      .replace(/\b(for|at)\s*$/i, '')
      .replace(/\s+/g, ' ')
      .trim();

    return description || 'Voice input transaction';
  }

  // Detect payment method
  detectPaymentMethod(text) {
    for (const [method, keywords] of Object.entries(this.paymentKeywords)) {
      if (keywords.some(keyword => text.includes(keyword))) {
        return method;
      }
    }
    return 'cash'; // Default
  }

  // Extract individual items from the transaction
  extractItems(text) {
    const items = [];
    
    // Look for "and" separated items
    const andSeparated = text.split(' and ');
    if (andSeparated.length > 1) {
      andSeparated.forEach(item => {
        const cleanItem = item.trim().replace(/^(purchase|bought|buy)\s+/i, '');
        if (cleanItem) items.push(cleanItem);
      });
    }

    // Look for comma separated items
    const commaSeparated = text.split(',');
    if (commaSeparated.length > 1) {
      commaSeparated.forEach(item => {
        const cleanItem = item.trim().replace(/^(purchase|bought|buy)\s+/i, '');
        if (cleanItem) items.push(cleanItem);
      });
    }

    return items.length > 0 ? items : [text];
  }

  // Categorize income transactions
  categorizeIncomeTransaction(text) {
    if (text.includes('director loan') || text.includes("director's loan") || text.includes('directors loan')) {
      return 'director_loan';
    }

    let bestMatch = null;
    let highestScore = 0;

    const canonicalMap = {
      nailServices: 'nails',
      hairServices: 'hair',
      skinServices: 'facial',
      injectables: 'injectables',
      bodyTreatments: 'body_treatments',
      makeupServices: 'makeup',
      massageServices: 'massage',
      otherServices: 'consulting',
      productSales: 'retail',
      govFunding: 'gov_funding',
      loanIncome: 'loan',
      directorLoanIncome: 'director_loan'
    };

    for (const [category, keywords] of Object.entries(this.categoryKeywords)) {
      // Only check income categories
      if (!INCOME_CATEGORY_KEYS.includes(category)) {
        continue;
      }

      const score = keywords.reduce((acc, keyword) => {
        return text.includes(keyword) ? acc + 1 : acc;
      }, 0);

      if (score > highestScore) {
        highestScore = score;
        bestMatch = category;
      }
    }

    return canonicalMap[bestMatch] || 'retail';
  }

  // Categorize expense transactions
  categorizeExpenseTransaction(text, items) {
    // If multiple items, try to categorize each
    if (items.length > 1) {
      return this.categorizeMultipleItems(items);
    }

    if (text.includes('director loan') || text.includes("director's loan") || text.includes('directors loan')) {
      return 'director_loan';
    }

    let bestMatch = null;
    let highestScore = 0;

    for (const [category, keywords] of Object.entries(this.categoryKeywords)) {
      // Skip income categories
      if (INCOME_CATEGORY_KEYS.includes(category)) {
        continue;
      }

      const score = keywords.reduce((acc, keyword) => {
        return text.includes(keyword) ? acc + 1 : acc;
      }, 0);

      if (score > highestScore) {
        highestScore = score;
        bestMatch = category;
      }
    }

    return bestMatch || 'misc';
  }

  // Handle multiple items in one transaction with smart categorization
  categorizeMultipleItems(items) {
    const normalizedItems = (items || [])
      .map((item) => {
        if (typeof item === 'string') return item;
        if (!item) return '';
        return item.description || item.item || item.name || '';
      })
      .filter((item) => item && item.trim().length > 0);

    const itemCategories = [];
    
    normalizedItems.forEach(item => {
      const itemText = item.toLowerCase().trim();
      let bestCategory = 'misc';
      let highestScore = 0;
      
      // Check each category for this specific item
      for (const [category, keywords] of Object.entries(this.categoryKeywords)) {
        // Skip income categories
        if (INCOME_CATEGORY_KEYS.includes(category)) {
          continue;
        }

        const score = keywords.reduce((acc, keyword) => {
          return itemText.includes(keyword) ? acc + 1 : acc;
        }, 0);

        if (score > highestScore) {
          highestScore = score;
          bestCategory = category;
        }
      }
      
      itemCategories.push({
        item: item,
        category: bestCategory,
        score: highestScore
      });
    });

    // Prioritize capital expenses (equipment) over supplies
    const hasCapitalExpense = itemCategories.some(item => 
      ['equipment', 'salon_tools', 'electronics', 'furniture'].includes(item.category)
    );
    
    if (hasCapitalExpense) {
      // If there are capital expenses, prioritize them
      const capitalItem = itemCategories.find(item => 
        ['equipment', 'salon_tools', 'electronics', 'furniture'].includes(item.category)
      );
      return capitalItem.category;
    }

    // Otherwise, return the most common category
    const categoryCount = {};
    itemCategories.forEach(item => {
      categoryCount[item.category] = (categoryCount[item.category] || 0) + 1;
    });

    return Object.keys(categoryCount).reduce((a, b) => 
      categoryCount[a] > categoryCount[b] ? a : b
    );
  }

  // Calculate confidence score
  calculateConfidence(result, text) {
    let confidence = 0;

    // Amount found
    if (result.amount > 0) confidence += 30;

    // Category matched
    if (result.category) confidence += 25;

    // Payment method detected
    if (result.paymentMethod !== 'cash') confidence += 15;

    // Transaction type confidence
    const typeKeywords = this.transactionTypeKeywords[result.type] || [];
    if (typeKeywords.some(keyword => text.includes(keyword))) {
      confidence += 20;
    }

    // Description quality
    if (result.description && result.description.length > 5) {
      confidence += 10;
    }

    return Math.min(confidence, 100);
  }

  // Get suggested categories for manual selection
  getSuggestedCategories(text, transactionType) {
    const suggestions = [];
    const categories = transactionType === 'income' 
      ? INCOME_CATEGORY_KEYS
      : Object.keys(this.categoryKeywords).filter(cat => 
          !INCOME_CATEGORY_KEYS.includes(cat)
        );

    categories.forEach(category => {
      const keywords = this.categoryKeywords[category] || [];
      const score = keywords.reduce((acc, keyword) => {
        return text.toLowerCase().includes(keyword) ? acc + 1 : acc;
      }, 0);

      if (score > 0) {
        suggestions.push({ category, score });
      }
    });

    return suggestions
      .sort((a, b) => b.score - a.score)
      .slice(0, 3)
      .map(s => s.category);
  }
}

export default new TransactionParser();
